import { SvgIcon, makeStyles } from '@material-ui/core';
import { format } from 'date-fns-tz';
import isNil from 'lodash/isNil';
import { inject, observer } from 'mobx-react';
import React from 'react';

import { Badge } from 'src/common/ui/components/Badge/Badge';
import { SHOW_BUSYNESS_DISPLAY } from 'src/featureFlags/currentFlags';
import { useFeatureFlag } from 'src/featureFlags/useFeatureFlag';
import { Staff_GetEventCardsQuery as StaffGetEventCardsQuery } from 'src/generated/gql/graphql';
import { SeePatientNowButton } from 'src/myDayToday/components/SeePatientNowButton';
import { getMotivationalQuote } from 'src/myDayToday/domain/motivationalPatientQuotesGenerator';
import useShowSeePatientNowButton from 'src/myDayToday/hooks/useShowSeePatientNowButton';
import { ReactComponent as IndividualIcon } from 'src/shared/icons/iconIndividual.svg';
import useRootStore from 'src/stores/useRootStore';
import { BoulderColors } from 'src/util/brand';

const TopBarComponent = ({
  currentTime,
  avgWaitTime,
  eventCardsData,
}: {
  currentTime: Date;
  avgWaitTime?: number;
  eventCardsData?: StaffGetEventCardsQuery['staff_myDayTodayEventCards'];
}) => {
  const styles = useStyles();
  const rootStore = useRootStore();
  const { provider } = rootStore.providers;
  const showBusynessDisplay = useFeatureFlag(SHOW_BUSYNESS_DISPLAY);

  const showSeePatientNowButton = useShowSeePatientNowButton();

  const { quote, author } = getMotivationalQuote();
  const waitTimeInfo = isNil(avgWaitTime) ? null : getWaitTimeInfo(avgWaitTime);

  return (
    <div className={styles.topBar}>
      <div className={styles.leftSection}>
        <div className={styles.welcome}>{getGreeting(currentTime, provider)}</div>
        <div className={styles.timeDetails}>
          <div className={styles.timeDisplay}>
            <div className={styles.time}>{format(currentTime, 'h:mm a z')}</div>
            <div className={styles.date}>{format(currentTime, 'EEEE, MMMM d')}</div>
          </div>
          {showBusynessDisplay && !!waitTimeInfo && waitTimeInfo.busyness !== 'moderate' && (
            <div className={styles.busynessDisplay}>
              <div className={styles.spacer} />
              <Badge
                color={waitTimeInfo.color}
                icon="waving"
                label={waitTimeInfo.text}
                tooltip={`Pop-In Clinic demand is ${waitTimeInfo.text.toLowerCase()} right now.`}
              />
            </div>
          )}
        </div>
      </div>
      <div className={styles.middleSection}>
        {showSeePatientNowButton && (
          <SeePatientNowButton
            seePatientDisabledReason={eventCardsData?.seePatientDisabledReason}
            seePatientRequestId={eventCardsData?.alternativeDropInCards[0]?.requestId}
          />
        )}
      </div>
      <div className={styles.rightSection}>
        <SvgIcon component={IndividualIcon} style={{ width: 48, height: 64 }} viewBox="0 0 48 64" />
        <blockquote>
          <p>“{quote}”</p>
          <footer>{author ? `- ${author}, Boulder Care Patient` : '- Boulder Care Patient'}</footer>
        </blockquote>
      </div>
    </div>
  );
};

type WaitTimeInfo = {
  text: string;
  color: 'info' | 'warning' | 'error';
  busyness?: 'low' | 'moderate' | 'high';
};

const getWaitTimeInfo = (avgWaitTime: number): WaitTimeInfo => {
  if (avgWaitTime <= 20) {
    return { text: 'Low', color: 'info', busyness: 'low' };
  } else if (avgWaitTime > 20 && avgWaitTime <= 50) {
    return { text: 'Moderate', color: 'warning', busyness: 'moderate' };
  } else {
    return { text: 'High', color: 'error', busyness: 'high' };
  }
};

const getGreeting = (
  date: Date,
  provider: { preferredName?: string | null; firstName?: string | null } | null,
): string => {
  let greeting: string;

  const hour = date.getHours();
  if (hour < 12) {
    greeting = 'Good morning';
  } else if (hour >= 12 && hour < 17) {
    greeting = 'Good afternoon';
  } else {
    greeting = 'Good evening';
  }

  const name = provider?.preferredName ?? provider?.firstName;
  if (name) {
    greeting = greeting.concat(' ', name);
  }

  return greeting.concat('!');
};

const useStyles = makeStyles({
  topBar: {
    backgroundColor: BoulderColors.Gray2,
    padding: '34px 46px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignSelf: 'stretch',
    boxSizing: 'border-box',

    fontFamily: '"Nunito", "Nunito Sans"',

    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 150,
    height: 150,
  },
  welcome: {
    fontSize: 24,
    fontWeight: 600,
    letterSpacing: 0.201,
    lineHeight: '145%',
    color: BoulderColors.Blue4,
  },
  timeDetails: {
    display: 'flex',
    flexDirection: 'row',
    gap: 140,
    color: BoulderColors.Gray5,
  },
  timeDisplay: {
    display: 'flex',
    flexDirection: 'column',
  },
  busynessDisplay: {
    display: 'flex',
    flexDirection: 'column',
  },
  time: {
    fontSize: 16,
    lineHeight: '145%',
    letterSpacing: 0.201,
  },
  date: {
    fontSize: 12,
    fontWeight: 700,
    lineHeight: '145%',
    letterSpacing: 1.44,
    textTransform: 'uppercase',
  },
  spacer: { flexGrow: 1 },
  leftSection: {
    flex: 25,
  },
  middleSection: {
    flex: 45,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-end',
    height: '100%',
  },
  rightSection: {
    display: 'flex',
    flex: 30,
    flexDirection: 'row',
    gap: 16,
    color: BoulderColors.Gray5,
    maxWidth: 500,

    '& blockquote': {
      display: 'flex',
      flexDirection: 'column',
      margin: 0,
      padding: 0,

      fontSize: 16,
      fontWeight: 600,
      lineHeight: '145%',
      letterSpacing: 0.201,
    },

    '& p': {
      padding: 0,
      margin: 0,
    },

    '& footer': {
      textTransform: 'uppercase',
      alignSelf: 'flex-end',

      fontSize: 12,
      fontWeight: 600,
      lineHeight: '145%',
      letterSpacing: 1.44,
    },
  },
});

export const LegacyTopBar = inject('rootStore')(observer(TopBarComponent));
